import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { CalendarCheck, CalendarDays, ClipboardList, StickyNote, MessageSquare } from "lucide-react";

const DAYS_FULL = { 1: "Δευτέρα", 2: "Τρίτη", 3: "Τετάρτη", 4: "Πέμπτη", 5: "Παρασκευή", 6: "Σάββατο", 0: "Κυριακή" };
const STATUS = {
  pending: ["Σε αναμονή", "bg-[#FFFBEB] text-[#92400E]"],
  approved: ["Εγκρίθηκε", "bg-[#EEF0EA] text-[#6E7A63]"],
  rejected: ["Απορρίφθηκε", "bg-[#FEF2F2] text-[#991B1B]"],
};
const card = "rounded-2xl bg-white border border-[#E3DED4] p-5 md:p-6";

const Head = ({ icon: Icon, title, to, link, testid }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="font-medium text-[#45423C] inline-flex items-center gap-2"><Icon className="h-4 w-4 text-[#C86235]" /> {title}</h2>
    {to && <Link to={to} data-testid={testid} className="text-xs font-semibold text-[#C86235] hover:underline">{link} →</Link>}
  </div>
);

function Tasks({ tasks }) {
  return (
    <div className={card} data-testid="my-tasks">
      <Head icon={ClipboardList} title="Εργασίες για σήμερα" />
      {tasks.length === 0 && <div className="text-sm text-[#8A8577]">Καμία εκκρεμής εργασία.</div>}
      <div className="space-y-2">
        {tasks.map((t) => (
          <Link key={t.id} to={t.kind === "visit" ? `/field-ops/${t.category || "housekeeping"}` : "/maintenance-tasks"} data-testid={`my-task-${t.id}`}
            className="flex items-center justify-between gap-3 rounded-xl bg-[#F7F5F1] px-3 py-2.5 text-sm hover:bg-[#F1EDE6]">
            <span className="text-[#45423C] font-medium truncate">{t.title}</span>
            <span className="text-xs text-[#6F6A60] whitespace-nowrap">{t.villa ? `${t.villa} · ` : ""}{t.due_date ? `έως ${fmtDate(t.due_date)}` : fmtDate(t.task_date)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Notes({ initial }) {
  const [text, setText] = useState(initial || "");
  useEffect(() => { setText(initial || ""); }, [initial]);
  const save = useMutation({
    mutationFn: async () => api.put("/my/notes", { text }),
    onSuccess: () => toast.success("Οι σημειώσεις αποθηκεύτηκαν"),
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className={card} data-testid="my-notes">
      <Head icon={StickyNote} title="Οι σημειώσεις μου" />
      <textarea data-testid="my-notes-text" rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="Γράψτε μια σημείωση…"
        className="w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]" />
      <button data-testid="my-notes-save" disabled={save.isPending || text === (initial || "")} onClick={() => save.mutate()}
        className="mt-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">Αποθήκευση</button>
    </div>
  );
}

function Messages() {
  const { data = [] } = useQuery({ queryKey: ["my-messages"], queryFn: async () => (await api.get("/notifications")).data });
  return (
    <div className={card} data-testid="my-messages">
      <Head icon={MessageSquare} title="Μηνύματα" />
      {data.length === 0 && <div className="text-sm text-[#8A8577]">Κανένα μήνυμα.</div>}
      <div className="space-y-2">
        {data.slice(0, 6).map((n) => (
          <div key={n.id} className={`rounded-xl px-3 py-2 text-sm ${n.read ? "bg-[#F7F5F1] text-[#6F6A60]" : "bg-[#FBF1ED] text-[#45423C]"}`} data-testid={`my-message-${n.id}`}>
            <div className="font-medium">{n.title || n.message}</div>
            {n.title && n.message && <div className="text-xs mt-0.5">{n.message}</div>}
            <div className="text-[10px] text-[#8A8577] mt-0.5">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Week({ wk }) {
  return (
    <div className={card} data-testid="my-week-shifts">
      <Head icon={CalendarDays} title={`Πρόγραμμα · ${Math.round(wk.hours || 0)} ώ.`} to="/schedule" link="Πλήρες πρόγραμμα" testid="my-schedule-link" />
      <div className="space-y-2">
        {(wk.shifts || []).map((s) => (
          <div key={s.dow} data-testid={`my-shift-${s.dow}`}
            className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm ${s.off ? "bg-[#F7F5F1] text-[#8A8577]" : "bg-[#45423C] text-white"}`}>
            <span>{DAYS_FULL[s.dow]}</span>
            <span className="tabular">{s.off ? "Ρεπό" : `${s.start} – ${s.end}`}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Leave({ lv }) {
  return (
    <div className={card} data-testid="my-leave-list">
      <Head icon={CalendarCheck} title={`Άδειες · υπόλοιπο ${lv.remaining ?? 0} ημ.`} to="/leave" link="Αίτηση άδειας" testid="my-leave-link" />
      <div className="text-xs text-[#6F6A60] mb-3" data-testid="my-stat-leave">{lv.used} από {lv.entitlement} ημέρες{lv.pending ? ` · ${lv.pending} σε αναμονή` : ""}</div>
      <div className="space-y-2">
        {(lv.requests || []).length === 0 && <div className="text-sm text-[#8A8577]">Καμία αίτηση άδειας.</div>}
        {(lv.requests || []).map((r) => {
          const [label, cls] = STATUS[r.status] || STATUS.pending;
          return (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-[#F1EDE6] pb-2">
              <span className="text-[#45423C]">{r.type_label}</span>
              <span className="text-[#6F6A60] tabular">{fmtDate(r.start_date)} – {fmtDate(r.end_date)} · {r.days} ημ.</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MyDashboard() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["my-dashboard"], queryFn: async () => (await api.get("/my/dashboard")).data });
  if (isLoading || !data) return <div className="h-64 flex items-center justify-center text-[#6F6A60]">Φόρτωση…</div>;
  if (!data.linked)
    return (
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-10 text-center text-[#6F6A60]" data-testid="my-dashboard-unlinked">
        Ο λογαριασμός σας δεν έχει συνδεθεί με καρτέλα υπαλλήλου. Επικοινωνήστε με τη διοίκηση.
      </div>
    );
  return (
    <div className="space-y-6" data-testid="my-dashboard">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Καλώς ήρθες, {user?.name}</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Η μέρα μου</h1>
        <p className="text-[#6F6A60] text-sm mt-1">{data.employee_name}{data.department ? ` · ${data.department}` : ""}{data.position ? ` · ${data.position}` : ""}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Tasks tasks={data.tasks || []} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Notes initial={data.notes} />
            <Messages />
          </div>
        </div>
        <div className="space-y-6">
          <Week wk={data.week || {}} />
          <Leave lv={data.leave || {}} />
        </div>
      </div>
    </div>
  );
}
